import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../../app/hooks";
import { selectUser } from "../../userProfile/userSlice";
import { sendMessage } from "../../shared/apiService";
import trackLead from "./trackLead";

const allowedSubject = "Aircraft disinsection";

export default function ContactForm() {
  const currentUser = useAppSelector(selectUser);
  const { search } = useLocation();
  const subject = new URLSearchParams(search).get("subject") === allowedSubject ? allowedSubject : "";
  const { register, handleSubmit, reset, watch, setFocus, formState: { errors } } = useForm({
    defaultValues: { firstName: "", lastName: "", email: "", phone: "", subject, message: "" },
  });
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const messageLength = (watch("message") || "").length;

  useEffect(() => {
    reset({
      firstName: currentUser?.first_name || "",
      lastName: currentUser?.last_name || "",
      email: currentUser?.email || "",
      phone: "",
      subject,
      message: "",
    });
  }, [currentUser?.first_name, currentUser?.last_name, currentUser?.email, subject, reset]);

  const submit = handleSubmit(async (data) => {
    if (sending || sent) return;
    setSending(true);
    setStatus("");
    try {
      await sendMessage(data);
      trackLead("contact-inquiry");
      setSent(true);
      setStatus("Thank you for getting in touch. Our team will review your message and contact you soon.");
    } catch (error) {
      setStatus("Your message could not be sent. Please try again or call +1 855-500-0538.");
    } finally {
      setSending(false);
    }
  }, (invalid) => {
    const first = Object.keys(invalid)[0];
    if (first) setFocus(first);
  });

  const field = (name, label, type = "text", options = {}) => {
    const id = `contact-${name}`;
    return <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type={type} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `${id}-error` : undefined} {...register(name, options)} />
      {errors[name] && <small className="error" id={`${id}-error`} role="alert">{errors[name].message}</small>}
    </div>;
  };

  return <form id="inquiry-form" onSubmit={submit} noValidate>
    <div className="contact-fields two">
      {field("firstName", "First name *", "text", { required: "Please complete this field.", maxLength: 120 })}
      {field("lastName", "Last name *", "text", { required: "Please complete this field.", maxLength: 120 })}
    </div>
    <div className="contact-fields two">
      {field("email", "Email *", "email", { required: "Please complete this field.", pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Please enter a valid email address." }, maxLength: 254 })}
      {field("phone", <span>Phone <span className="optional">Optional</span></span>, "tel", { maxLength: 25 })}
    </div>
    {field("subject", "Subject *", "text", { required: "Please complete this field.", maxLength: 120 })}
    <div className="field">
      <label htmlFor="contact-message">Message *</label>
      <textarea id="contact-message" rows="6" maxLength="500" placeholder="Tell us the aircraft, location, timing, and what you need." aria-invalid={Boolean(errors.message)} aria-describedby={`message-help${errors.message ? " contact-message-error" : ""}`} {...register("message", { required: "Please complete this field.", maxLength: 500 })} />
      <small id="message-help">Include enough detail for our team to route your question. {messageLength}/500 characters.</small>
      {errors.message && <small className="error" id="contact-message-error" role="alert">{errors.message.message}</small>}
    </div>
    <p className="contact-privacy">We’ll use these details to respond to your inquiry. See our <a href="/privacy-policy">Privacy Policy</a>.</p>
    <div className="submit-row"><button className="button" type="submit" disabled={sending || sent}>{sending ? "Sending…" : sent ? "Message sent" : "Send message"}</button>{status && <p role={sent ? "status" : "alert"} aria-live="polite">{status}</p>}</div>
  </form>;
}
